import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ReactPlayer from 'react-player';
import { ThumbsUp, ThumbsDown, MessageCircle, Share2, Volume2, VolumeX, X, Flag, Play, ChevronUp, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { useReelsFeed } from '../../hooks/post/useReelsFeed';
import { markVideoSeen } from '../../hooks/post/reels-seen';
import { postService } from '../../services/post.service';
import { voteUpCountAPost, voteDownCountAPost } from '../../api/posts/fetch-post.api';
import { reportPostApi } from '../../api/posts/report-post.apt';

const REPORT_REASONS = [
  { label: 'Spam', value: 0 },
  { label: 'Nội dung không phù hợp', value: 1 },
  { label: 'Bạo lực', value: 3 },
  { label: 'Quấy rối', value: 4 },
  { label: 'Nội dung tình dục', value: 9 },
  { label: 'Khác', value: 10 },
];
import { IPostModel } from '../../models';
import { RootState } from '../../configuration';
import { CommentsComponent } from '../../components';
import { useReelAds } from './use-reel-ads';
import CreatorAdCard from '../../components/ads/creator-ad-card';
import PreRollAd from '../../components/ads/pre-roll-ad';
import { AdCreative } from '../../api/ads/ads.api';
import { useReelPreRoll, withoutPreRolled } from './use-reel-pre-roll';
import {
  buildReelDisplayList,
  displayKey,
  findDisplayIndexByPostId,
  postAtDisplayIndex,
} from './reels-display-list';

// Player dọc kiểu Reels/TikTok: vuốt lên/xuống qua từng nội dung, trộn xen kẽ video + post.
// GĐ2: bó nội dung vào cột giữa (đẹp trên màn rộng), rải video đều, mở bình luận dạng bottom-sheet.

const COLUMN = 'max-w-[500px]'; // bó nội dung vào 1 cột kiểu TikTok/FB web

const compact = (n: number): string =>
  n >= 1000
    ? (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace('.0', '').replace('.', ',') + 'K'
    : String(n);

const GRADIENTS = [
  'from-indigo-600 to-fuchsia-600',
  'from-rose-500 to-orange-500',
  'from-emerald-500 to-teal-600',
  'from-sky-500 to-indigo-600',
  'from-violet-600 to-pink-600',
  'from-amber-500 to-red-600',
];
const gradientFor = (id: string) => {
  let s = 0;
  for (let i = 0; i < id.length; i++) s += id.charCodeAt(i);
  return GRADIENTS[s % GRADIENTS.length];
};

interface ReelItemProps {
  post: IPostModel;
  index: number;
  active: boolean;
  near: boolean;
  muted: boolean;
  onToggleMute: () => void;
  onOpenComments: () => void;
  /** Reel dừng chờ quảng cáo chèn trước (đang hỏi hoặc đang chạy). */
  hold?: boolean;
  /** Quảng cáo chèn trước đang chạy trên reel này. */
  preRollAd?: AdCreative | null;
  onPreRollStart?: () => void;
  onPreRollFinish?: (started: boolean) => void;
}

const ReelItem = ({
  post,
  index,
  active,
  near,
  muted,
  onToggleMute,
  onOpenComments,
  hold = false,
  preRollAd = null,
  onPreRollStart,
  onPreRollFinish,
}: ReelItemProps) => {
  const [expanded, setExpanded] = useState(false);
  // Video dọc/vuông → 'cover' (lấp đầy); ngang (meme) → 'contain' (không cắt). Đo khi player sẵn sàng.
  const [fit, setFit] = useState<'cover' | 'contain'>('contain');
  const playerRef = useRef<ReactPlayer>(null);

  // Like / dislike TOGGLE: bấm = like, bấm lại = bỏ like (tương tự dislike); like & dislike loại trừ nhau.
  const [liked, setLiked] = useState(!!post.currentUserHasVoteUp);
  const [disliked, setDisliked] = useState(!!post.currentUserHasVoteDown);
  const [likeCount, setLikeCount] = useState(Number(post.voteUpCount || 0));
  const [dislikeCount, setDislikeCount] = useState(Number(post.voteDownCount || 0));
  const [paused, setPaused] = useState(false); // bấm vào video = tạm dừng (không phải tắt tiếng)
  const [reportOpen, setReportOpen] = useState(false);
  const [reporting, setReporting] = useState(false);

  // Cuộn sang video khác → bỏ trạng thái tạm dừng để video mới tự phát.
  useEffect(() => {
    if (!active) setPaused(false);
  }, [active]);

  const submitReport = async (reasonValue: number) => {
    setReporting(true);
    try {
      await reportPostApi({
        postId: post.id,
        onwerPostReportId: (post as any).userId || '',
        content: '',
        reportPostValue: reasonValue,
      });
      toast.success('Đã gửi báo cáo. Cảm ơn bạn!');
      setReportOpen(false);
    } catch {
      toast.error('Báo cáo thất bại, thử lại.');
    } finally {
      setReporting(false);
    }
  };

  const doLike = () => {
    if (liked) {
      setLiked(false);
      setLikeCount((c) => Math.max(0, c - 1));
    } else {
      setLiked(true);
      setLikeCount((c) => c + 1);
      if (disliked) {
        setDisliked(false);
        setDislikeCount((c) => Math.max(0, c - 1));
      }
    }
    voteUpCountAPost({ postId: post.id }).catch(() => {});
  };
  const doDislike = () => {
    if (disliked) {
      setDisliked(false);
      setDislikeCount((c) => Math.max(0, c - 1));
    } else {
      setDisliked(true);
      setDislikeCount((c) => c + 1);
      if (liked) {
        setLiked(false);
        setLikeCount((c) => Math.max(0, c - 1));
      }
    }
    voteDownCountAPost({ postId: post.id }).catch(() => {});
  };

  const onVideoReady = () => {
    const v = playerRef.current?.getInternalPlayer() as
      | HTMLVideoElement
      | undefined;
    if (v?.videoWidth) {
      setFit(v.videoHeight >= v.videoWidth * 0.95 ? 'cover' : 'contain');
    }
  };

  const isVideo =
    post.postType === 'video' && (post.postVideoResponses?.length || 0) > 0;
  const imageUrl = post.postImageResponses?.[0]?.url;
  const hasMedia = isVideo || !!imageUrl;
  const caption = post.content || post.title || '';

  // Chạm vào video = TẠM DỪNG / phát lại (tắt tiếng có nút riêng góc phải).
  const onMediaTap = () => {
    if (isVideo) setPaused((p) => !p);
  };

  return (
    <div
      data-index={index}
      className="relative h-[100dvh] w-full snap-start snap-always bg-black flex justify-center"
    >
      <div className={`relative h-full w-full ${COLUMN} bg-black overflow-hidden`}>
        {/* ===== Nội dung ===== */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          onClick={onMediaTap}
        >
          {isVideo ? (
            near ? (
              <div
                className={`h-full w-full [&_video]:h-full [&_video]:w-full ${
                  fit === 'cover'
                    ? '[&_video]:object-cover'
                    : '[&_video]:object-contain'
                }`}
              >
                <ReactPlayer
                  ref={playerRef}
                  onReady={onVideoReady}
                  url={post.postVideoResponses[0]?.url}
                  playing={active && !paused && !hold}
                  muted={muted}
                  loop
                  width="100%"
                  height="100%"
                  config={{
                    file: {
                      attributes: {
                        playsInline: true,
                        'webkit-playsinline': 'true',
                      },
                    },
                  }}
                />
              </div>
            ) : (
              <div
                className={`h-full w-full bg-gradient-to-br ${gradientFor(
                  post.id
                )} opacity-60`}
              />
            )
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt=""
              className="h-full w-full object-contain"
              loading={near ? 'eager' : 'lazy'}
            />
          ) : (
            // Post chữ → thẻ chữ to trên nền gradient (kiểu quote)
            <div
              className={`h-full w-full bg-gradient-to-br ${gradientFor(
                post.id
              )} flex items-center justify-center p-8`}
            >
              <p
                className={`text-white text-2xl md:text-3xl font-semibold leading-relaxed text-center whitespace-pre-wrap ${
                  expanded ? 'overflow-y-auto max-h-full' : 'line-clamp-[10]'
                }`}
              >
                {caption || '...'}
              </p>
            </div>
          )}
        </div>

        {/* quảng cáo chèn trước: xong thì reel chạy lại từ đầu */}
        {isVideo && active && preRollAd ? (
          <PreRollAd
            key={preRollAd.creativeId}
            ad={preRollAd}
            topClass="top-16"
            onStart={onPreRollStart}
            onFinish={(_, started) => {
              playerRef.current?.seekTo(0, 'seconds');
              onPreRollFinish?.(started);
            }}
          />
        ) : null}

        {/* lớp phủ tối dưới cho dễ đọc */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent" />

        {/* biểu tượng khi TẠM DỪNG (bấm vào video) */}
        {isVideo && paused && active && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
            <div className="rounded-full bg-black/45 p-5">
              <Play size={44} className="text-white" fill="currentColor" />
            </div>
          </div>
        )}

        {/* nút tắt/mở tiếng (chỉ video) */}
        {isVideo && (
          <button
            onClick={onToggleMute}
            className="absolute top-4 right-4 z-20 rounded-full bg-black/40 p-2 text-white"
            aria-label={muted ? 'Bật tiếng' : 'Tắt tiếng'}
          >
            {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
        )}

        {/* (Cột nút phải đã chuyển ra ngoài cột video — xem cuối wrapper) */}

        {/* Sheet báo cáo */}
        {reportOpen && (
          <div
            className="absolute inset-0 z-40 flex items-end justify-center bg-black/50"
            onClick={(e) => {
              e.stopPropagation();
              setReportOpen(false);
            }}
          >
            <div
              className="w-full max-w-[500px] rounded-t-2xl bg-white dark:bg-dark2 p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="font-semibold ui-text mb-3">Báo cáo nội dung</div>
              <div className="space-y-1">
                {REPORT_REASONS.map((r) => (
                  <button
                    key={r.value}
                    disabled={reporting}
                    onClick={() => submitReport(r.value)}
                    className="w-full text-left px-3 py-2.5 rounded-lg ui-text hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setReportOpen(false)}
                className="w-full mt-2 px-3 py-2.5 rounded-lg text-center ui-muted"
              >
                Huỷ
              </button>
            </div>
          </div>
        )}

        {/* ===== Tác giả + caption ===== */}
        <div className="absolute left-4 right-16 sm:right-4 bottom-6 z-20 text-white">
          <div className="flex items-center gap-2 mb-2">
            {post.userAvatar && (
              <img
                src={post.userAvatar}
                alt=""
                className="h-9 w-9 rounded-full object-cover border border-white/50"
              />
            )}
            <span className="font-semibold">
              {post.userFullName || post.username}
            </span>
          </div>
          {/* Post chữ: nội dung đã nằm ở thẻ giữa → dưới chỉ để tên. Post media: hiện caption + xem thêm. */}
          {hasMedia && caption && (
            <p
              className={`text-sm text-white/90 whitespace-pre-wrap ${
                expanded ? '' : 'line-clamp-2'
              }`}
              onClick={() => setExpanded((e) => !e)}
            >
              {caption}
            </p>
          )}
        </div>
      </div>

      {/* ===== Cột nút phải (kiểu IG): desktop đặt SÁT NGOÀI mép phải video (~14px, right = calc(50%-308px));
           mobile không có chỗ ngoài → nằm trong mép phải video (right-3), cao hơn để không đè caption. ===== */}
      <div className="absolute z-30 right-3 bottom-28 sm:right-[calc(50%-308px)] sm:bottom-6 flex flex-col items-center gap-5 text-white">
        <button onClick={doLike} className="flex flex-col items-center">
          <ThumbsUp
            size={32}
            className={liked ? 'text-sky-400' : 'text-white'}
            fill={liked ? 'currentColor' : 'none'}
          />
          <span className="text-xs mt-1">{compact(likeCount)}</span>
        </button>
        <button onClick={doDislike} className="flex flex-col items-center">
          <ThumbsDown
            size={32}
            className={disliked ? 'text-rose-400' : 'text-white'}
            fill={disliked ? 'currentColor' : 'none'}
          />
          <span className="text-xs mt-1">{compact(dislikeCount)}</span>
        </button>
        <button onClick={onOpenComments} className="flex flex-col items-center">
          <MessageCircle size={32} />
          <span className="text-xs mt-1">
            {compact(Number(post.commentsCount || 0))}
          </span>
        </button>
        <button
          onClick={() => {
            navigator.clipboard
              ?.writeText(`${window.location.origin}/videos/${post.id}`)
              .then(() => toast.success('Đã copy link'))
              .catch(() => {});
          }}
          className="flex flex-col items-center"
        >
          <Share2 size={30} />
          <span className="text-xs mt-1">Chia sẻ</span>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setReportOpen(true);
          }}
          className="flex flex-col items-center"
        >
          <Flag size={30} />
          <span className="text-xs mt-1">Báo cáo</span>
        </button>
      </div>
    </div>
  );
};

const ReelsPage = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const { data, hasMore, loading } = useReelsFeed({ pageNumber });
  const [activeKey, setActiveKey] = useState<string | null>(null); // theo khoá: chèn ô quảng cáo không làm lệch
  const [muted, setMuted] = useState(true);
  const [commentPost, setCommentPost] = useState<IPostModel | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startId = searchParams.get('start');
  const didInitialScroll = useRef(false);
  const viewedRef = useRef<Set<string>>(new Set());
  const profile = useSelector(
    (state: RootState) => state.features.auth.profile
  );

  const items = data;
  // Danh sách hiển thị: reel trộn ô quảng cáo (chỉ khi đã lấy được quảng cáo); activeIndex/children/data-index theo danh sách này.
  const activePostId = activeKey?.startsWith('ad-') ? activeKey.slice(3) : activeKey;
  const activePostIdx = Math.max(0, items.findIndex((p) => p.id === activePostId));
  const ads = useReelAds(items, activePostIdx, profile?.userId);
  // Reel đang chạy thật (đang đứng ở thẻ quảng cáo thì không có reel nào chạy).
  const onAdCard = !!activeKey?.startsWith('ad-');
  const activeReel = onAdCard ? null : items[activePostIdx] ?? null;
  const preRoll = useReelPreRoll(activeReel, activePostIdx, profile?.userId);
  // Reel ở ô quảng cáo đã có quảng cáo chèn trước chạy thì không chèn thẻ quảng cáo sau nó nữa; không có thì giữ thẻ như cũ.
  const display = useMemo(
    () => buildReelDisplayList(items, withoutPreRolled(ads, preRoll.preRolled)),
    [items, ads, preRoll.preRolled]
  );
  const displayRef = useRef(display);
  displayRef.current = display;
  const activeIndex = Math.max(
    0,
    activeKey ? display.findIndex((d) => displayKey(d) === activeKey) : 0
  );

  // Tự tải thêm khi gần cuối luồng.
  useEffect(() => {
    if (activeIndex >= display.length - 3 && hasMore && !loading) {
      setPageNumber((p) => p + 1);
    }
  }, [activeIndex, display.length, hasMore, loading]);

  // Item vào khung: (1) URL nhảy theo video hiện tại (share được link), (2) đánh dấu đã xem +
  // ghi lượt xem 1 lần/bài (chưa đăng nhập → 401 → nuốt lỗi).
  useEffect(() => {
    const post = postAtDisplayIndex(display, activeIndex); // thẻ quảng cáo: không đếm xem, không đổi URL
    if (!post) return;
    window.history.replaceState(null, '', `/reels?start=${post.id}`);
    if (viewedRef.current.has(post.id)) return;
    viewedRef.current.add(post.id);
    const isVideo =
      post.postType === 'video' && (post.postVideoResponses?.length || 0) > 0;
    if (isVideo) markVideoSeen(post.id);
    postService.increaseViewCount(post.id).catch(() => {});
  }, [activeIndex, display]);

  // Theo dõi item đang trong khung nhìn (play/pause + windowing).
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio >= 0.6) {
            const idx = Number((e.target as HTMLElement).dataset.index);
            const d = displayRef.current[idx];
            if (!Number.isNaN(idx) && d) setActiveKey(displayKey(d));
          }
        });
      },
      { root, threshold: [0.6] }
    );
    const kids = Array.from(root.children).filter(
      (c) => (c as HTMLElement).dataset.index != null
    );
    kids.forEach((k) => obs.observe(k));
    return () => obs.disconnect();
  }, [display.length]);

  // ESC thoát trình Reels (nếu đang mở bình luận thì đóng bình luận trước).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (commentPost) setCommentPost(null);
      else navigate(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [commentPost, navigate]);

  // Nhảy tới bài mở từ feed (double-tap ?start=<id>).
  useEffect(() => {
    if (didInitialScroll.current || !startId || display.length === 0) return;
    const idx = findDisplayIndexByPostId(display, startId);
    if (idx >= 0) {
      const el = containerRef.current?.children[idx] as HTMLElement | undefined;
      el?.scrollIntoView();
      setActiveKey(displayKey(display[idx]));
      didInitialScroll.current = true;
    }
  }, [startId, display]);

  return (
    <div className="fixed inset-0 z-[100] bg-black">
      <button
        onClick={() => navigate(-1)}
        // z-40: luôn nổi trên lớp phủ quảng cáo chèn trước (z-30) để người xem vẫn thoát được.
        className="absolute top-4 left-4 z-40 rounded-full bg-black/40 p-2 text-white"
        aria-label="Đóng"
      >
        <X size={22} />
      </button>

      {/* Điều hướng trên/dưới kiểu IG/FB: mũi tên ở RÌA PHẢI màn hình (bỏ rail "Tiếp theo" bên trái —
          IG/FB không có, gây lệch/cấn). Cuộn tới reel trước/kế. */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 z-30 hidden sm:flex flex-col gap-3">
        <button
          disabled={activeIndex <= 0}
          onClick={() => {
            const el = containerRef.current?.children[activeIndex - 1] as HTMLElement | undefined;
            el?.scrollIntoView({ behavior: 'smooth' });
          }}
          className="rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-25 p-3 text-white transition"
          aria-label="Reel trước"
        >
          <ChevronUp size={22} />
        </button>
        <button
          disabled={activeIndex >= display.length - 1}
          onClick={() => {
            const el = containerRef.current?.children[activeIndex + 1] as HTMLElement | undefined;
            el?.scrollIntoView({ behavior: 'smooth' });
          }}
          className="rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-25 p-3 text-white transition"
          aria-label="Reel kế"
        >
          <ChevronDown size={22} />
        </button>
      </div>

      <div
        ref={containerRef}
        className="h-[100dvh] w-full overflow-y-scroll snap-y snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {display.map((d, i) =>
          d.kind === 'ad' ? (
            <div
              key={d.key}
              data-index={i}
              className="relative h-[100dvh] w-full snap-start snap-always bg-black flex justify-center"
            >
              <div className={`relative h-full w-full ${COLUMN} overflow-hidden`}>
                {Math.abs(i - activeIndex) <= 2 && (
                  <CreatorAdCard
                    variant="reel"
                    initialAd={d.ad}
                    placement="Reels"
                    postId={d.afterPost.id}
                    ownerId={(d.afterPost as any).userId}
                  />
                )}
              </div>
            </div>
          ) : (
            <ReelItem
              key={d.post.id}
              post={d.post}
              index={i}
              active={i === activeIndex}
              near={Math.abs(i - activeIndex) <= 1}
              muted={muted}
              onToggleMute={() => setMuted((m) => !m)}
              onOpenComments={() => setCommentPost(d.post)}
              hold={preRoll.holds(d.post, d.postIndex)}
              preRollAd={i === activeIndex ? preRoll.adFor(d.post.id) : null}
              onPreRollStart={() => preRoll.started(d.post.id)}
              onPreRollFinish={(started) => preRoll.finish(d.post.id, started)}
            />
          )
        )}
        {loading && (
          <div className="h-16 flex items-center justify-center text-white/70">
            Đang tải…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="h-[100dvh] flex items-center justify-center text-white/70">
            Chưa có nội dung.
          </div>
        )}
      </div>

      {/* ===== Panel bình luận (bên PHẢI — dễ đọc hơn hiển thị dưới video) ===== */}
      {commentPost && (
        <div
          className="fixed inset-0 z-[110] flex justify-end bg-black/50"
          onClick={() => setCommentPost(null)}
        >
          <div
            className="h-full w-full sm:w-[400px] flex flex-col bg-white dark:bg-boxdark shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-700 px-4 py-3">
              <span className="font-semibold text-slate-800 dark:text-white">
                Bình luận
              </span>
              <button
                onClick={() => setCommentPost(null)}
                className="text-slate-500"
                aria-label="Đóng bình luận"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <CommentsComponent
                post={commentPost}
                userId={profile?.userId || ''}
                profileUser={!!profile}
                pageSize={10}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReelsPage;
